export const POSTHOG_CROSS_DOMAIN_DISTINCT_ID_PARAM = "distinct_id";
export const POSTHOG_CROSS_DOMAIN_SESSION_ID_PARAM = "session_id";

export interface PostHogCrossDomainHashParams {
  distinctId: string | null;
  sessionId: string | null;
}

/**
 * Parse PostHog cross-domain hash params without mutating the URL.
 * Supports `#distinct_id=...&session_id=...` alongside other hash fragments (e.g. Clerk `#/verify`).
 */
export function parsePostHogCrossDomainHash(hash: string): PostHogCrossDomainHashParams {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) {
    return { distinctId: null, sessionId: null };
  }

  // Clerk hash routing uses paths like `/verify-email-address`; only treat as query params otherwise.
  if (raw.startsWith("/")) {
    return { distinctId: null, sessionId: null };
  }

  const params = new URLSearchParams(raw);
  const distinctId = params.get(POSTHOG_CROSS_DOMAIN_DISTINCT_ID_PARAM)?.trim() || null;
  const sessionId = params.get(POSTHOG_CROSS_DOMAIN_SESSION_ID_PARAM)?.trim() || null;
  return { distinctId, sessionId };
}

/**
 * Remove PostHog cross-domain params from a hash string; returns `#` prefix or empty string.
 */
export function stripPostHogCrossDomainHash(hash: string): string {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw || raw.startsWith("/")) {
    return hash.startsWith("#") ? hash : raw ? `#${raw}` : "";
  }

  const params = new URLSearchParams(raw);
  params.delete(POSTHOG_CROSS_DOMAIN_DISTINCT_ID_PARAM);
  params.delete(POSTHOG_CROSS_DOMAIN_SESSION_ID_PARAM);

  const remaining = params.toString();
  if (!remaining) {
    return "";
  }
  return `#${remaining}`;
}

export function hasPostHogCrossDomainHashParams(hash: string): boolean {
  const parsed = parsePostHogCrossDomainHash(hash);
  return Boolean(parsed.distinctId || parsed.sessionId);
}
